// #873 v2 — per-user Cards sub-page.
//
// Moved here from AdminUserConversations after the v1 ship: Bas's
// staging review surfaced that the per-topic charts alone can't
// answer "did the fox come up?" because the wildcard segmenter LLM
// emits unique inferred_topic strings per segment, so almost every
// topic has count=1 and rolls into "Other".
//
// This page pairs the four aggregate charts (still useful for the
// rough shape) with a filterable per-card grid below — substring
// match across the card's display name AND the wildcard topics from
// the same call. Typing "fox" surfaces every card row whose call
// touched anything fox-shaped, which is the actual drill-in admin
// needs.
//
// Both halves share a single endpoint round-trip (the section lifts
// its data up via onDataChange).
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import { userSubPageStyles } from './userSubPageStyles';
import TopicActivitySection from './TopicActivitySection';

// v1.1.0 — #886: flat outcome taxonomy. The pre-#886 (outcome,
// skip_reason) two-field shape collapsed into FIVE flat outcome
// states (played / declined / ignored / passed_over / unraised).
// Outcome-balance pie now has 6 slices (the five flat states +
// wildcard); the per-card grid shows the flat outcome directly
// (no skip_reason sub-line) and the type-bucket badge moves to a
// row of its own.
// v1.0.2 — staging iteration: split skipped out of declined (90%
// of unplayed rows are skipped, not user-rejected — Bas hit on the
// v2.1 staging review). Outcome-balance pie now has 4 slices
// (green=played / amber=skipped / red=declined / blue=wildcard),
// grid surfaces dealer_notes.skip_reason under skipped rows, and
// resolves display names for ALL card_keys not just played ones
// (latent GUID bug for declined health-concern cards).
// v1.2.0 (#963) — Card-type balance pie gains a distinct
// `Reminiscence` slice (reminisce_* content + probe cards) alongside
// the existing conversation / game / multimedia / event / news /
// wildcard wedges.
// v1.3.0 — #981: conversation-steering toggle (suggested / guided).
// Admin-only A/B knob surfaced right where the wildcard-ratio charts
// are, so Bas can flip a user to the "play by the playbook" steered
// rhythm and feel the difference. Backwards-compat default 'suggested'.
const VERSION = 'v1.3.0';

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const TYPE_BADGE_COLORS = {
  conversation: { bg: '#FFF1E8', fg: '#B84A1E' },
  game:         { bg: '#F1E8FF', fg: '#5B2E8E' },
  multimedia:   { bg: '#E8FFF5', fg: '#0B6B4F' },
  event:        { bg: '#FFF5E0', fg: '#7A5B00' },
  other:        { bg: '#EEEEEE', fg: '#555' },
};

function CardsGrid({ cards, truncated }) {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    if (!cards) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter(c => {
      if ((c.display || '').toLowerCase().includes(q)) return true;
      if ((c.card_key || '').toLowerCase().includes(q)) return true;
      for (const wt of (c.wildcard_topics || [])) {
        if ((wt || '').toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [cards, filter]);

  if (!cards || cards.length === 0) {
    return (
      <div className="empty-state">
        No card-outcomes in this window.
      </div>
    );
  }

  return (
    <>
      <style>{`
        .cg-filter-row {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 12px; flex-wrap: wrap;
        }
        .cg-filter-row input {
          padding: 7px 12px; border: 1px solid #ccc; border-radius: 6px;
          font-size: 0.88rem; font-family: inherit; min-width: 240px;
        }
        .cg-filter-row .cg-hint { color: #888; font-size: 0.8rem; }
        .cg-filter-row .cg-count { color: #2C2C2C; font-weight: 600; font-size: 0.85rem; margin-left: auto; }
        .cg-truncated { color: #B84A1E; font-size: 0.8rem; margin-top: 6px; font-style: italic; }
        .badge-type {
          display: inline-block; padding: 2px 8px; border-radius: 12px;
          font-size: 0.72rem; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .badge-outcome {
          display: inline-block; padding: 2px 8px; border-radius: 12px;
          font-size: 0.72rem; font-weight: 600;
        }
        .badge-played      { background: #E5F8EB; color: #1F7A3E; }
        .badge-declined    { background: #FCE7E4; color: #9B2A1F; }
        .badge-ignored     { background: #FFF6E5; color: #7A5B00; }
        .badge-passed_over { background: #F3E5F5; color: #6A1B9A; }
        .badge-unraised    { background: #ECEFF1; color: #546E7A; }
        .cg-segmenter-chip {
          display: inline-block; margin-left: 6px;
          padding: 1px 7px; border-radius: 10px;
          background: #FFF6E5; color: #7A5B00;
          font-size: 0.7rem; font-weight: 600;
          letter-spacing: 0.03em; cursor: help;
        }
      `}</style>

      <div className="cg-filter-row">
        <input
          type="text"
          placeholder="Filter by topic… (e.g. fox)"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          aria-label="Filter cards by topic"
        />
        <span className="cg-hint">
          Searches the card display name + raw key. (Wildcard topics
          from the same call also match silently, so a topic that
          only appeared as off-script chat still surfaces its row.)
        </span>
        <span className="cg-count">
          {filtered.length} / {cards.length} shown
        </span>
      </div>

      <div className="data-grid">
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Card</th>
              <th>Type</th>
              <th>Outcome</th>
              <th>Conversation</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="empty-state">
                  No rows match the filter.
                </td>
              </tr>
            )}
            {filtered.map(c => {
              const tColor = TYPE_BADGE_COLORS[c.type] || TYPE_BADGE_COLORS.other;
              return (
                <tr key={c.card_outcome_id}>
                  <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {fmtDateTime(c.dealt_at)}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.display}</div>
                    <div style={{ color: '#888', fontSize: '0.75rem',
                                  fontFamily: 'ui-monospace, monospace' }}>
                      {c.card_key}
                    </div>
                  </td>
                  <td>
                    <span className="badge-type" style={{
                      background: tColor.bg, color: tColor.fg,
                    }}>
                      {c.type}
                    </span>
                  </td>
                  <td>
                    <span className={`badge-outcome badge-${c.outcome}`}>
                      {c.outcome}
                    </span>
                    {c.outcome_source === 'segmenter' && (
                      <span
                        className="cg-segmenter-chip"
                        title={
                          'Dealer never marked CARD_PLAYED for this card, but the ' +
                          'post-call segmenter recognised it as played in the ' +
                          'transcript. Reconciled as played.'
                        }
                      >
                        per segmenter
                      </span>
                    )}
                    {/* #886 — skip_reason removed (flat outcome carries
                        the full state semantics). The outcome badge
                        itself shows ignored / passed_over / unraised
                        directly. */}
                  </td>
                  <td>
                    {c.conversation_id ? (
                      <Link to={`/admin/conversations/${c.conversation_id}`}
                            style={{ color: '#E8845A', fontSize: '0.82rem',
                                     fontFamily: 'ui-monospace, monospace' }}>
                        {c.conversation_id.slice(0, 8)} →
                      </Link>
                    ) : (
                      <span style={{ color: '#aaa' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {truncated && (
        <div className="cg-truncated">
          Showing the most recent 500 rows in this window. Narrow the window
          (Week vs. Month) or step back through previous buckets to see older data.
        </div>
      )}
    </>
  );
}

// #981 — conversation-steering toggle. Default 'suggested' (|| keeps it
// backwards-compatible if the backend response predates the field).
function SteeringToggle({ userId, value, onChange }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const current = value || 'suggested';

  async function setMode(mode) {
    if (mode === current || saving) return;
    setSaving(true);
    setErr(null);
    try {
      await adminApi.setConversationSteering(userId, mode);
      onChange(mode);
    } catch (e) {
      setErr(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="steering-toggle">
      <style>{`
        .steering-toggle {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
          margin: 4px 0 20px; padding: 12px 16px;
          background: #FAF7F2; border: 1px solid #E7DECE; border-radius: 8px;
        }
        .steering-toggle .st-label { font-weight: 600; font-size: 0.9rem; color: #2C2C2C; }
        .steering-toggle .st-btns { display: inline-flex; border-radius: 6px; overflow: hidden; border: 1px solid #ccc; }
        .steering-toggle .st-btns button {
          border: none; padding: 7px 16px; font-size: 0.85rem; font-family: inherit;
          cursor: pointer; background: #fff; color: #444;
        }
        .steering-toggle .st-btns button + button { border-left: 1px solid #ccc; }
        .steering-toggle .st-btns button.active { background: #E8845A; color: #fff; font-weight: 600; }
        .steering-toggle .st-btns button:disabled { cursor: default; opacity: 0.7; }
        .steering-toggle .st-hint { color: #888; font-size: 0.78rem; flex-basis: 100%; }
        .steering-toggle .st-err { color: #B84A1E; font-size: 0.8rem; }
      `}</style>
      <span className="st-label">Conversation steering</span>
      <span className="st-btns">
        <button
          className={current === 'suggested' ? 'active' : ''}
          disabled={saving}
          onClick={() => setMode('suggested')}
        >
          Suggested
        </button>
        <button
          className={current === 'guided' ? 'active' : ''}
          disabled={saving}
          onClick={() => setMode('guided')}
        >
          Guided
        </button>
      </span>
      {saving && <span className="st-hint">Saving…</span>}
      {err && <span className="st-err">Error: {err}</span>}
      <span className="st-hint">
        <strong>Suggested</strong> = today's behaviour (cards as a gentle
        indication). <strong>Guided</strong> = "play by the playbook" —
        Berta opens organically, plays ~half the dealt hand, takes a
        detour or two, then finishes the hand, with a larger hand to pace.
        Applies from the user's second call onward.
      </span>
    </div>
  );
}

export default function AdminUserCards() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await adminApi.getUser(id);
        if (cancelled) return;
        setUser(u);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <AdminLayout title="Cards">
      <style>{userSubPageStyles}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
          </div>
          <div className="page-subtitle">
            {user.email} · Cards sub-page · interaction-card history + per-call drill-down
          </div>
        </div>
      )}

      {user && (
        <SteeringToggle
          userId={id}
          value={user.conversation_steering}
          onChange={(mode) => setUser((u) => ({ ...u, conversation_steering: mode }))}
        />
      )}

      {user && (
        <TopicActivitySection userId={id} onDataChange={setActivity} />
      )}

      {user && activity && !activity.empty && (
        <>
          <div className="section-title">
            Cards in this window
            <span className="section-sub">
              One row per dealt card. Filter to drill in on a topic.
            </span>
          </div>
          <CardsGrid
            cards={activity.cards || []}
            truncated={!!activity.cards_truncated}
          />
        </>
      )}
    </AdminLayout>
  );
}
